import { format } from 'date-fns';
import {
  ArrowLeft,
  ChevronDown,
  Download,
  Hospital,
  UserRoundPlus,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@predoc/cow';
import { PageHeader } from '@/components/PageHeader';
import { triggerDownload } from '@/utils/downloadUtils';
import { getProfileColor } from '@/utils/helpers';

interface FacesheetViewerHeaderProps {
  title: string;
  generatedAt?: Date;
  patientFullName?: string;
  profileName?: string;
  profileAvatarType?: string;
  canDownload?: boolean;
  isReferenceView?: boolean;
  allReferencesDownloadUrl?: string;
  currentReferenceDownload?: {
    signedUrl: string;
    pageNumber?: number | null;
  };
  backAriaLabel?: string;
  onBack?: () => void;
}

export const FacesheetViewerHeader = ({
  title,
  generatedAt,
  patientFullName,
  profileName,
  profileAvatarType,
  canDownload = false,
  isReferenceView = false,
  allReferencesDownloadUrl,
  currentReferenceDownload,
  backAriaLabel = 'Close document viewer',
  onBack,
}: FacesheetViewerHeaderProps) => {
  const currentReferenceHref = currentReferenceDownload
    ? `${currentReferenceDownload.signedUrl}${currentReferenceDownload.pageNumber ? `#page=${currentReferenceDownload.pageNumber}` : ''}`
    : undefined;

  return (
    <PageHeader>
      <div className="flex items-center gap-3">
        {onBack ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label={backAriaLabel}
            className="size-9"
          >
            <ArrowLeft className="size-5" />
          </Button>
        ) : null}
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      </div>

      <div className="flex items-center gap-6">
        {generatedAt ? (
          <span className="text-sm text-gray-600">
            Generated {format(generatedAt, 'MM/dd/yyyy')}
          </span>
        ) : null}
        {patientFullName ? (
          <span className="flex items-center gap-1.5 text-sm text-gray-900">
            <UserRoundPlus className="size-4" />
            {patientFullName}
          </span>
        ) : null}
        {profileName ? (
          <span className="flex items-center gap-1.5 text-sm text-gray-900">
            <Hospital
              className="size-4"
              style={{ color: getProfileColor(profileAvatarType || '') }}
            />
            {profileName}
          </span>
        ) : null}
        {canDownload ? (
          isReferenceView ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="flex items-center gap-2"
                  aria-label="Download"
                >
                  <Download className="size-4" />
                  Download
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                {allReferencesDownloadUrl ? (
                  <DropdownMenuItem asChild>
                    <a
                      href={allReferencesDownloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      All References
                    </a>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem disabled>All References</DropdownMenuItem>
                )}
                {currentReferenceHref ? (
                  <DropdownMenuItem asChild>
                    <a
                      href={currentReferenceHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Current Reference
                    </a>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem disabled>
                    Current Reference
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="flex items-center gap-2"
              disabled={!allReferencesDownloadUrl}
              onClick={() => {
                if (allReferencesDownloadUrl) {
                  triggerDownload(allReferencesDownloadUrl);
                }
              }}
              aria-label="Download all references"
            >
              <Download className="size-4" />
              Download
            </Button>
          )
        ) : null}
      </div>
    </PageHeader>
  );
};
